import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { Concession } from '../../lib/api';
import { DialogState, ApprovalFormState } from './types';

interface ActionDialogsProps {
  concession: Concession;
  dialogs: DialogState;
  rejectionReason: string;
  approvalForm: ApprovalFormState;
  actionLoading: boolean;
  onDialogsChange: (dialogs: DialogState) => void;
  onRejectionReasonChange: (value: string) => void;
  onApprovalFormChange: (form: ApprovalFormState) => void;
  onApprove: () => void;
  onReject: () => void;
}

export function ActionDialogs({
  concession,
  dialogs,
  rejectionReason,
  approvalForm,
  actionLoading,
  onDialogsChange,
  onRejectionReasonChange,
  onApprovalFormChange,
  onApprove,
  onReject,
}: ActionDialogsProps) {
  return (
    <>
      {/* Reject Dialog */}
      <Dialog open={dialogs.reject} onOpenChange={(open) => onDialogsChange({ ...dialogs, reject: open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Concession</DialogTitle>
            <DialogDescription>
              The parts stay out of specification and must not be shipped against
              this order. Your reason is sent to the supplier.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => onRejectionReasonChange(e.target.value)}
              rows={4}
            />
            {/* CR-6510: the decision is recorded against whoever is signed in;
                the supplier sees a named rejection, not an anonymous one. */}
            <p className="text-xs text-muted-foreground">
              Recorded against your name and department, dated today.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onDialogsChange({ ...dialogs, reject: false })}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onReject}
              disabled={actionLoading || !rejectionReason.trim()}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Reject Concession
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={dialogs.approve} onOpenChange={(open) => onDialogsChange({ ...dialogs, approve: open })}>
        {/* The base DialogContent is centred with translate-y and sets no
            max-height, so a form taller than the viewport bleeds off both
            edges with nothing to scroll. This one grew past that when the
            Approval Process block was added. Cap it and scroll the body
            only — the footer has to stay put, because a reviewer must always
            be able to reach Cancel and Approve. */}
        <DialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Approve Concession</DialogTitle>
            <DialogDescription>
              Approving accepts these parts against the stated deviation. The
              supplier is notified and the order line is tagged so procurement
              can see the parts were accepted out of specification.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto -mx-6 px-6 py-4 space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="quantity_approved">Quantity Approved (optional)</Label>
              <Input
                id="quantity_approved"
                type="number"
                step="any"
                min="0"
                placeholder={`Default: ${concession.quantity_affected}`}
                value={approvalForm.quantityApproved}
                onChange={(e) => onApprovalFormChange({ ...approvalForm, quantityApproved: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Approve fewer than the {concession.quantity_affected} affected if only
                part of the batch is acceptable. Leave blank to approve them all.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="valid_until">Valid Until (optional)</Label>
              <Input
                id="valid_until"
                type="date"
                value={approvalForm.validUntil}
                onChange={(e) => onApprovalFormChange({ ...approvalForm, validUntil: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                After this date the concession no longer covers deliveries. Leave
                blank if it applies to this batch only, with no time limit.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="approval_notes">Approval Notes (optional)</Label>
              <Textarea
                id="approval_notes"
                placeholder="Enter any approval notes..."
                value={approvalForm.notes}
                onChange={(e) => onApprovalFormChange({ ...approvalForm, notes: e.target.value })}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Any conditions attached to the approval. Visible to the supplier.
              </p>
            </div>

            {/* CR-6510: the legacy form's "Approval Process" block. Every
                field is optional — left blank, the server records the signing
                user's own name, job title and department and today's date, so
                the common case stays a two-click approval. They are here for
                the case the paper form existed for: someone other than the
                account holder (an MRB chair, a design authority) signs, and
                the audit needs to name them. */}
            <div className="border-t pt-4 space-y-4">
              <div>
                <p className="font-medium text-sm">Approval Process</p>
                <p className="text-xs text-muted-foreground">
                  Leave blank to record your own name, position and department, dated today.
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="qn_mrb_reference">QN / MRB Reference</Label>
                <Input
                  id="qn_mrb_reference"
                  placeholder="e.g. QN-2026-0087"
                  value={approvalForm.qnMrbReference}
                  onChange={(e) => onApprovalFormChange({ ...approvalForm, qnMrbReference: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Your own quality notification or Material Review Board number for
                  this decision, so it can be traced back to your records. Optional —
                  leave blank if you do not raise one.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="approver_name">Name</Label>
                  <Input
                    id="approver_name"
                    placeholder="Who signed this off"
                    value={approvalForm.approverName}
                    onChange={(e) => onApprovalFormChange({ ...approvalForm, approverName: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="approver_position">Position</Label>
                  <Input
                    id="approver_position"
                    placeholder="Their job title"
                    value={approvalForm.approverPosition}
                    onChange={(e) => onApprovalFormChange({ ...approvalForm, approverPosition: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="approver_department">Department</Label>
                  <Input
                    id="approver_department"
                    placeholder="Signing on behalf of"
                    value={approvalForm.approverDepartment}
                    onChange={(e) => onApprovalFormChange({ ...approvalForm, approverDepartment: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="customer_response_date">Customer Response Date</Label>
                  <Input
                    id="customer_response_date"
                    type="date"
                    value={approvalForm.customerResponseDate}
                    onChange={(e) => onApprovalFormChange({ ...approvalForm, customerResponseDate: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onDialogsChange({ ...dialogs, approve: false })}>
              Cancel
            </Button>
            <Button onClick={onApprove} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Approve Concession
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
